import { useEffect, useMemo, useRef, useState } from 'react';
import { useData } from '../lib/data';
import { supabase } from '../lib/supabase';
import { READONLY } from '../lib/readonly';
import type { Project } from '../lib/database.types';

// Generate a slug from a display name: lowercase, non-alphanumerics → dashes,
// collapse, trim. Matches the shape of existing tb_projects.slug values.
function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

// A big, clickable project picker. Replaces the old <select> dropdown which
// Andrew called out as too small. Renders as a button that opens a
// searchable popover list of projects, grouped by client.
//
// Supports inline creation of a new build via the "+ new build" footer —
// writes directly to tb_projects and optimistically adds the new row to
// the local DataProvider store.
export function ProjectPicker({
  value,
  onChange,
  projects: scoped,
  placeholder = 'pick a build…',
  defaultClientSlug,
}: {
  value: string;
  onChange: (slug: string) => void;
  projects?: Project[];
  placeholder?: string;
  // When set, "+ new build" creates a project under this client by default.
  // Otherwise the new-build form shows a client picker.
  defaultClientSlug?: string | null;
}) {
  const { projects: allProjects, clients, clientBySlug, upsertProjectLocal } = useData();
  const pool = scoped ?? allProjects;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [mode, setMode] = useState<'list' | 'create'>('list');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // New-build form state
  const inferredClient = useMemo(() => {
    if (defaultClientSlug !== undefined) return defaultClientSlug;
    // If all scoped projects share one client, that's the inferred parent.
    if (!scoped || scoped.length === 0) return null;
    const first = scoped[0].client_slug;
    return scoped.every((p) => p.client_slug === first) ? first : null;
  }, [scoped, defaultClientSlug]);
  const [newName, setNewName] = useState('');
  const [newClient, setNewClient] = useState<string | null>(inferredClient);
  const [newDescription, setNewDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    if (open && mode === 'list') setTimeout(() => inputRef.current?.focus(), 10);
  }, [open, mode]);

  useEffect(() => {
    if (!open) {
      setMode('list');
      setQuery('');
      setNewName('');
      setNewDescription('');
      setNewClient(inferredClient);
      setCreateError(null);
      return;
    }
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, inferredClient]);

  const selected = pool.find((p) => p.slug === value);

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? pool.filter(
          (p) =>
            p.name.toLowerCase().includes(q) ||
            p.slug.toLowerCase().includes(q) ||
            (p.description ?? '').toLowerCase().includes(q),
        )
      : pool;
    const map = new Map<string, Project[]>();
    for (const p of filtered) {
      const key = p.client_slug ?? '_automations';
      const arr = map.get(key) ?? [];
      arr.push(p);
      map.set(key, arr);
    }
    return Array.from(map.entries()).sort(([a], [b]) => {
      if (a === '_automations') return 1;
      if (b === '_automations') return -1;
      return a.localeCompare(b);
    });
  }, [pool, query]);

  const createBuild = async () => {
    if (READONLY) {
      setCreateError('readonly mode — writes disabled');
      return;
    }
    const name = newName.trim();
    if (!name) {
      setCreateError('name is required');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const baseSlug = slugify(name);
      if (!baseSlug) {
        setCreateError('name must contain at least one alphanumeric character');
        setCreating(false);
        return;
      }
      // Resolve collisions by appending -2, -3, … until we find a free slot.
      let candidate = baseSlug;
      let suffix = 2;
      while (allProjects.some((p) => p.slug === candidate)) {
        candidate = `${baseSlug}-${suffix++}`;
        if (suffix > 50) {
          setCreateError('too many slug collisions, pick a different name');
          setCreating(false);
          return;
        }
      }
      const row: Project = {
        slug: candidate,
        name,
        client_slug: newClient,
        engagement_stage: null,
        state: 'building',
        priority: 5,
        description: newDescription.trim(),
        category: null,
      };
      const { data, error } = await supabase
        .from('tb_projects')
        .insert(row)
        .select()
        .single();
      if (error) {
        setCreateError(error.message);
        setCreating(false);
        return;
      }
      const created = (data as Project | null) ?? row;
      upsertProjectLocal(created);
      onChange(created.slug);
      setOpen(false);
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'unknown error');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 bg-panelHi border border-border rounded-md
                   text-left text-sm min-h-[44px]
                   hover:border-borderHi focus:outline-none focus:border-amber/60 transition-colors"
      >
        <span className={'flex-1 truncate ' + (selected ? 'text-ink' : 'text-dim')}>
          {selected ? selected.name : placeholder}
        </span>
        <span className="text-dim text-xs">▾</span>
      </button>

      {open && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 panel shadow-hud
                     max-h-[60vh] overflow-hidden flex flex-col"
        >
          {mode === 'list' && (
            <>
              <div className="p-2 border-b border-border">
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="search builds…"
                  className="w-full px-3 py-2 bg-panelHi border border-border rounded text-sm
                             placeholder-dim focus:outline-none focus:border-amber/60"
                />
              </div>
              <div className="flex-1 overflow-y-auto">
                {grouped.length === 0 && (
                  <div className="p-4 text-center text-xs text-dim font-mono">no matches</div>
                )}
                {grouped.map(([clientSlug, projs]) => {
                  const label =
                    clientSlug === '_automations'
                      ? 'AUTOMATIONS'
                      : clientBySlug[clientSlug]?.display_name ?? clientSlug;
                  return (
                    <div key={clientSlug}>
                      <div className="px-3 py-1.5 text-[10px] font-mono text-amberHi uppercase tracking-widest bg-panel sticky top-0">
                        {label}
                      </div>
                      {projs.map((p) => {
                        const isSelected = p.slug === value;
                        return (
                          <button
                            key={p.slug}
                            type="button"
                            onClick={() => {
                              onChange(p.slug);
                              setOpen(false);
                              setQuery('');
                            }}
                            className={
                              'w-full px-4 py-3 text-left flex items-center gap-2 transition-colors border-l-2 ' +
                              (isSelected
                                ? 'bg-amber/15 border-amber'
                                : 'border-transparent hover:bg-panelHi')
                            }
                          >
                            <div className="flex-1 min-w-0">
                              <div className="text-sm truncate">{p.name}</div>
                              {p.description && (
                                <div className="text-[10px] text-dim font-mono truncate mt-0.5">
                                  {p.description}
                                </div>
                              )}
                            </div>
                            {isSelected && <span className="text-amberHi text-xs">✓</span>}
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
              <div className="border-t border-border bg-panel">
                <button
                  type="button"
                  onClick={() => {
                    // Seed the name with the current query so searching for
                    // something that doesn't exist and hitting "+ new build"
                    // just works.
                    if (query.trim()) setNewName(query.trim());
                    setMode('create');
                  }}
                  disabled={READONLY}
                  className="w-full px-4 py-3 text-left text-sm font-mono text-amberHi
                             hover:bg-panelHi transition-colors
                             disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  + new build
                </button>
              </div>
            </>
          )}

          {mode === 'create' && (
            <div className="p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-mono text-amberHi uppercase tracking-widest">
                  New build
                </div>
                <button
                  type="button"
                  onClick={() => setMode('list')}
                  className="text-xs font-mono text-dim hover:text-ink"
                >
                  ← back
                </button>
              </div>
              <input
                autoFocus
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="build name…"
                className="w-full px-3 py-2 bg-panelHi border border-border rounded text-sm
                           placeholder-dim focus:outline-none focus:border-amber/60"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !creating) createBuild();
                }}
              />
              <select
                value={newClient ?? ''}
                onChange={(e) => setNewClient(e.target.value || null)}
                className="w-full px-3 py-2 bg-panelHi border border-border rounded text-sm
                           text-ink focus:outline-none focus:border-amber/60"
              >
                <option value="">— no client (AUTOMATIONS) —</option>
                {clients
                  .slice()
                  .sort((a, b) => a.display_name.localeCompare(b.display_name))
                  .map((c) => (
                    <option key={c.slug} value={c.slug}>
                      {c.display_name}
                    </option>
                  ))}
              </select>
              <input
                type="text"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="description (optional)"
                className="w-full px-3 py-2 bg-panelHi border border-border rounded text-sm
                           placeholder-dim focus:outline-none focus:border-amber/60"
              />
              <div className="text-[10px] font-mono text-dim">
                slug preview: <span className="text-amberHi">{slugify(newName) || '—'}</span>
              </div>
              {createError && (
                <div className="text-xs font-mono text-red border border-red/40 bg-red/10 rounded p-2">
                  {createError}
                </div>
              )}
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setMode('list')}
                  className="btn text-xs"
                >
                  cancel
                </button>
                <button
                  type="button"
                  onClick={createBuild}
                  disabled={creating || !newName.trim() || READONLY}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold
                             bg-amber border border-amber text-bg
                             hover:brightness-110 transition
                             disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {creating ? 'creating…' : 'create build'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
